import { ListType } from '../constants/enums'
import { LISTS } from '../state'
import { getStateUser, resolveID, resolveSlug } from '../state/getState'
import { findNextOrPrevNodeID } from '../utilities/listTraversal'

type RetType<R extends number> = R extends 1 ? string : number

/**
 * Get the next or previous model ID in the list. If no list is provided, `DEFAULT_PUBLIC` is used.
 * @param slugOrId Slug or ID of the current model, or `null` to use the first model in the list.
 * @returns The ID of the next/previous model in the list. To return the slug instead, set option `returnSlug` to 1.
 */
export function getNextOrPreviousModel<R extends number>(
    slugOrId?: string | number | null,
    options: {
        list?: ListType
        previous?: boolean
        returnSlug?: R
    } = {},
): RetType<R> {
    const { list = ListType.DEFAULT_PUBLIC, previous, returnSlug } = options
    const currentID = resolveID(slugOrId)
    const nextOrPreviousID = findNextOrPrevNodeID(
        currentID,
        LISTS[list],
        LISTS[ListType.DEFAULT],
        previous,
    )
    return formatResult(nextOrPreviousID, returnSlug)
}

/**
 * Used to provide TypeScript return type inference for `getNextOrPreviousModel`.
 * @param id Model ID
 * @param returnSlug True to return the model slug instead of the model ID
 * @returns if `returnSlug` is true, returns the model slug; otherwise, returns the model ID
 */
function formatResult<R extends number>(id: number, returnSlug?: R) {
    if (!id) return undefined
    if (!returnSlug) return id as RetType<R>
    return resolveSlug(id) as RetType<R>
}

export const getSafeNextOrPreviousSurrogate = (
    surrogateId: number,
    { previous = false, shouldGet = false } = {},
): number => {
    const unusable = shouldGet || !surrogateId
    const usableId = unusable
        ? getNextOrPreviousModel(surrogateId, { previous })
        : surrogateId
    const surrogate = getStateUser(usableId)

    if (surrogate?.banned) {
        // keep going until we find a non-banned surrogate
        const nextSurrogateID = getNextOrPreviousModel(usableId, {
            previous,
        })
        return getSafeNextOrPreviousSurrogate(nextSurrogateID, { previous })
    } else return usableId
}
